import { P, propertyById } from './kestrelOntology';
import { PENDING_KEYS, SAFETY_INSTRUCTIONS, packPassport } from './kestrelPassport';
import { snowflakeAt } from './time';

/*
 * What Home's hero story draws beyond the shared Kestrel dataset: the pack
 * unit's passport before its last two values are in (its rated capacity, a
 * measurement in kWh, and its safety instructions, a short text written in
 * English and German). The public page the Composer shapes is in
 * kestrelHeroPage.js (only the story's later parts draw it). Neither value
 * is reported to the EU registry (no data_element_id).
 */

/** The two values the hero fills in, as typed. */
export const HERO_VALUES = Object.freeze({
    /** Rated capacity, typed into the measurement input (the stored number is 75.2). */
    capacity: '75.2',
    /** Safety instructions, English first, then German. */
    safety: SAFETY_INSTRUCTIONS,
});

/** The measurement the hero fills: the pack's rated capacity. */
export const HERO_MEASURE = P.ratedCapacity;
/** The text the hero fills: its safety instructions. */
export const HERO_TEXT = P.safetyInstructions;

/** German where it reads differently; a maker's name, a serial or a chemistry reads the same. */
const GERMAN = {
    [P.plant]: 'Gigafactory Göteborg, SE',
    [P.cellFormat]: 'Zylindrisch 4680',
};

/**
 * A level's values as the hero's pages read them: every text written per
 * language (short and long text) in English and German, so the German
 * public page shows nothing in English (and no "EN" mark beside it).
 */
export function inGerman(data) {
    return Object.fromEntries(Object.entries(data).map(([id, entry]) => {
        const property = propertyById(id);
        if (!['short_text', 'long_text'].includes(property?.type) || typeof entry?.value !== 'string') return [id, entry];

        return [id, { ...entry, value: { en: entry.value, de: GERMAN[id] ?? entry.value } }];
    }));
}

const change = (sequence, property, oldValue, newValue, at, extra = {}) => ({
    change_id: snowflakeAt('chg', '2026-09-08T10:12:30Z', sequence),
    property,
    old_value: oldValue,
    new_value: newValue,
    updated_at: at.naive,
    ...extra,
});

/**
 * The pack unit's `passportData` at a point in the hero's story:
 *
 * - `measured`: the rated capacity is saved (75.2 kWh).
 * - `texted`: the safety instructions are saved, in English and German (one
 *   save carries both languages, as the drawer does).
 * - `released`: released as #1, which locks it.
 *
 * Before any of it the pack is a draft at 13 of its 15 values, last touched
 * 12 minutes ago: everything but those two came in with its import three
 * weeks ago, a correction two days ago and a supplier request since. The
 * defaults are the end of the story.
 */
export function heroPassport(clock, { measured = true, texted = true, released = true } = {}) {
    const draft = packPassport(clock, { released: false });
    const data = inGerman(draft.data);
    delete data[P.ratedCapacity];
    delete data[P.safetyInstructions];

    // The shared draft's history without the two values (they come in during
    // the story), and with the values that arrived since moved to earlier in
    // the day, so the draft was last touched 12 minutes ago.
    const arrivals = PENDING_KEYS.map((key) => P[key]);
    const history = draft.changelog
        .filter((entry) => entry.property !== P.ratedCapacity && entry.property !== P.safetyInstructions)
        .map((entry) => {
            const arrival = arrivals.indexOf(entry.property);
            if (arrival === -1 || entry.old_value !== null) return entry;

            return { ...entry, updated_at: clock.ago({ minutes: 50 - arrival * 6 }).naive };
        })
        .sort((a, b) => a.updated_at.localeCompare(b.updated_at));

    const changelog = [...history];
    if (measured) {
        data[P.ratedCapacity] = { value: 75.2 };
        changelog.push(change(40, P.ratedCapacity, null, 75.2, clock.ago({ seconds: 40 })));
    }
    if (texted) {
        data[P.safetyInstructions] = { value: { ...SAFETY_INSTRUCTIONS } };
        changelog.push(change(41, P.safetyInstructions, null, { ...SAFETY_INSTRUCTIONS }, clock.ago({ seconds: 20 })));
    }
    if (released) {
        changelog.push(change(42, 'publish_status', 'draft', 'released', clock.ago({ seconds: 4 }), { release_number: 1 }));
    }

    return {
        ...draft,
        data,
        changelog,
        publish_status: released ? 'released' : 'draft',
        release_number: released ? 1 : 0,
        meta: { ...draft.meta, updated_at: changelog[changelog.length - 1].updated_at },
    };
}

/**
 * heroPassport for one clock, remembered per stage: the story asks for the
 * same few stages on every step.
 */
export function heroPassports(clock) {
    const stages = new Map();

    return ({ measured = true, texted = true, released = true } = {}) => {
        const key = `${measured}|${texted}|${released}`;
        if (!stages.has(key)) stages.set(key, heroPassport(clock, { measured, texted, released }));

        return stages.get(key);
    };
}
